import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  Text,
  useToast,
  Heading,
  HStack,
  IconButton,
  Tooltip,
  Box,
  Button,
  Flex,
  Portal,
  keyframes,
} from '@chakra-ui/react';
import {
  faLink,
  faRss,
  faStop,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { SharedLinkDeepFragment } from '../../../gql/graphql';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { ago } from '../../../utils/time-utils';

// Define the pulsing animation
const pulseOpacity = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

interface ManageSharedLinksPopoverProps {
  orderName: string;
  activeLinks: SharedLinkDeepFragment[];
  onRevokeLink: (linkId: string) => Promise<void>;
}

export default function ManageSharedLinksPopover({
  orderName,
  activeLinks,
  onRevokeLink,
}: ManageSharedLinksPopoverProps) {
  const { t } = useTranslation(['project']);
  const toast = useToast();

  const pulseAnimation = `${pulseOpacity} 2s infinite ease-in-out`;

  return (
    <Popover placement="bottom-start" isLazy>
      {() => (
        <>
          <PopoverTrigger>
            <Box
              as={IconButton}
              aria-label={t('order-item.shared-links.manage-tooltip')}
              size="xs"
              variant="ghost"
              colorScheme="blue"
              marginY="auto"
              marginRight={1}
              animation={pulseAnimation}
            >
              <Tooltip
                label={t('order-item.shared-links.manage-tooltip')}
                openDelay={400}
              >
                <SparkelIcon icon={faRss} color="blue.300" />
              </Tooltip>
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent>
              <PopoverBody paddingX={1} paddingY={2} zIndex={1000}>
                <Heading
                  as={'h3'}
                  size={'xs'}
                  marginY={1}
                  marginX={2}
                  noOfLines={1}
                >
                  {t('project:order-item.shared-links.popover-title', {
                    orderName: orderName,
                  })}
                </Heading>
                {activeLinks.length === 0 ? (
                  <Text fontSize="sm" paddingX={2} paddingBottom={2}>
                    {t('project:order-item.shared-links.no-active-links')}
                  </Text>
                ) : (
                  <VStack spacing={0} align="stretch" marginTop={1}>
                    {activeLinks.map((link, idx) => (
                      <Box
                        key={link.id}
                        role="group"
                        paddingY={2}
                        paddingX={3}
                        borderBottomWidth={
                          idx < activeLinks.length - 1 ? '1px' : '0px'
                        }
                        borderColor="gray.200"
                        _dark={{ borderColor: 'gray.600' }}
                        _hover={{
                          bgColor: 'gray.50',
                          _dark: {
                            bgColor: 'gray.700',
                          },
                        }}
                        overflow="hidden"
                      >
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          minHeight="38px"
                          position="relative"
                        >
                          <Box flexGrow={1} mr={0} overflow="hidden">
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              noOfLines={1}
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              width="100%"
                              title={
                                link.name ||
                                t(
                                  'project:order-item.shared-links.link-created-on-title',
                                  {
                                    date: new Date(
                                      link.createdAt
                                    ).toLocaleDateString(),
                                  }
                                )
                              }
                            >
                              {link.name ||
                                t(
                                  'project:order-item.shared-links.link-created-on',
                                  {
                                    date: new Date(
                                      link.createdAt
                                    ).toLocaleDateString(),
                                  }
                                )}
                            </Text>
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              _dark={{ color: 'gray.400' }}
                              noOfLines={1}
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              width="100%"
                              title={`${t(
                                'project:order-item.shared-links.expires-prefix'
                              )} ${new Date(link.expiresAt).toLocaleString()}`}
                            >
                              {t(
                                'project:order-item.shared-links.expires-prefix'
                              )}{' '}
                              {ago(link.expiresAt)}
                            </Text>
                          </Box>
                          <HStack
                            spacing={1}
                            opacity={0}
                            visibility="hidden"
                            _groupHover={{
                              opacity: 1,
                              visibility: 'visible',
                            }}
                            transition="opacity 0.1s ease-in-out, visibility 0.1s ease-in-out"
                            position="absolute"
                            right={-2}
                          >
                            <Tooltip
                              label={t(
                                'project:order-item.shared-links.copy-tooltip'
                              )}
                              openDelay={300}
                            >
                              <Button
                                aria-label={t(
                                  'project:order-item.shared-links.copy-tooltip'
                                )}
                                leftIcon={<SparkelIcon icon={faLink} />}
                                size="xs"
                                colorScheme="gray"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `${window.location.origin}/view/${link.initialStateId}`
                                  );
                                  toast({
                                    title: t(
                                      'project:order-item.shared-links.toast.copied-title'
                                    ),
                                    status: 'success',
                                    duration: 2000,
                                    isClosable: true,
                                  });
                                }}
                              >
                                {t(
                                  'project:order-item.shared-links.copy-tooltip'
                                )}
                              </Button>
                            </Tooltip>
                            <Tooltip
                              label={t(
                                'project:order-item.shared-links.revoke-tooltip'
                              )}
                              openDelay={300}
                            >
                              <IconButton
                                aria-label={t(
                                  'project:order-item.shared-links.revoke-tooltip'
                                )}
                                icon={
                                  <SparkelIcon icon={faStop} color="red.400" />
                                }
                                size="xs"
                                colorScheme="gray"
                                onClick={async () => {
                                  await onRevokeLink(link.id);
                                }}
                              />
                            </Tooltip>
                          </HStack>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                )}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
}
